import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ModalCloseButton } from '@/components/ModalCloseButton'
import { LoadingScreen } from '@/components/LoadingScreen'
import {
  useJoinManagerLeague,
  useManagerHome,
  usePublicManagerLeagues,
  type ManagerHome,
  type ManagerHomeLeague,
  type ManagerLeaguePreview,
  type ManagerPendingAction,
} from '@/features/manager/api'
import { useTranslation } from '@/i18n'

export function ManagerPage() {
  const { t } = useTranslation()
  const [isLeagueDiscoveryOpen, setIsLeagueDiscoveryOpen] = useState(false)
  const managerQuery = useManagerHome()
  const publicLeaguesQuery = usePublicManagerLeagues()
  const home = managerQuery.data

  if (managerQuery.isLoading) {
    return (
      <div className="space-y-6">
        <LoadingScreen compact />
        <ManagerPageSkeleton />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {managerQuery.isError ? (
        <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-6 sm:p-8">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-accent-300">{t('Golero Manager')}</p>
          <h1 className="mt-3 text-2xl font-black tracking-tight text-white">{t('Manager niet geladen')}</h1>
          <p className="mt-2 max-w-xl text-sm leading-6 text-surface-400">
            {t('De Manager-overzichtsgegevens konden niet worden opgehaald.')}
          </p>
          <button
            type="button"
            onClick={() => void managerQuery.refetch()}
            className="mt-5 rounded-xl bg-accent-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-accent-500"
          >
            {t('Opnieuw proberen')}
          </button>
        </section>
      ) : null}

      {home ? (
        <ManagerDashboard
          home={home}
          availableLeagueCount={publicLeaguesQuery.data?.pages[0]?.total}
          onOpenDiscovery={() => setIsLeagueDiscoveryOpen(true)}
        />
      ) : null}
      {isLeagueDiscoveryOpen ? <ManagerLeagueDiscoverySheet onClose={() => setIsLeagueDiscoveryOpen(false)} /> : null}
    </div>
  )
}

function ManagerLeagueDiscoverySheet({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  const [isClosing, setIsClosing] = useState(false)

  function requestClose() {
    setIsClosing(true)
  }

  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') requestClose()
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', handleEscape)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleEscape)
    }
  }, [])

  useEffect(() => {
    if (!isClosing) return

    const timeout = window.setTimeout(onClose, 300)
    return () => window.clearTimeout(timeout)
  }, [isClosing, onClose])

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        onClick={requestClose}
        aria-label={t('Sluit Manager-league selectie')}
        className={`${isClosing ? 'animate-drawer-backdrop-out' : 'animate-drawer-backdrop-in'} absolute inset-0 bg-black/80 backdrop-blur-sm`}
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="manager-league-discovery-title"
        className={`${isClosing ? 'animate-manager-player-picker-out' : 'animate-manager-player-picker-in'} absolute inset-x-0 bottom-0 mx-auto flex h-[min(78dvh,720px)] max-h-[90dvh] w-full max-w-3xl flex-col overflow-hidden rounded-t-[1.5rem] border-t border-white/[0.1] bg-[#0d1113] shadow-[0_-20px_64px_rgba(0,0,0,0.5)]`}
      >
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-white/[0.07] p-5 sm:p-6">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-accent-200">{t('Ontdekken')}</p>
            <h2 id="manager-league-discovery-title" className="mt-1 text-xl font-black tracking-tight text-white sm:text-2xl">
              {t('Doe mee aan een Manager-league')}
            </h2>
            <p className="mt-2 text-sm text-surface-400">{t('Kies een openbare competitie en begin met je eigen team.')}</p>
          </div>
          <ModalCloseButton onClick={requestClose} label={t('Sluiten')} className="h-9 w-9" />
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto p-5 sm:p-6">
          <ManagerLeagueDiscoveryContent />
        </div>
      </section>
    </div>
  )
}

function ManagerLeagueDiscoveryContent() {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const publicLeaguesQuery = usePublicManagerLeagues(query)
  const items = publicLeaguesQuery.data?.pages.flatMap((page) => page.data) ?? []
  const loadMoreRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const target = loadMoreRef.current
    if (!target || !publicLeaguesQuery.hasNextPage) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !publicLeaguesQuery.isFetchingNextPage) {
          void publicLeaguesQuery.fetchNextPage()
        }
      },
      { rootMargin: '240px' },
    )

    observer.observe(target)
    return () => observer.disconnect()
  }, [
    publicLeaguesQuery.fetchNextPage,
    publicLeaguesQuery.hasNextPage,
    publicLeaguesQuery.isFetchingNextPage,
  ])

  return (
    <div>
      <label className="flex w-full items-center gap-2 rounded-xl border border-white/[0.08] bg-[#111719] px-3 py-2.5 focus-within:border-accent-500/60">
        <span className="text-surface-500" aria-hidden="true">⌕</span>
        <span className="sr-only">{t('Zoek Manager-league')}</span>
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('Zoek een league...')}
          className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-white outline-none placeholder:text-surface-500"
        />
      </label>

      {publicLeaguesQuery.isLoading && !publicLeaguesQuery.data ? <p className="mt-5 text-sm text-surface-500">{t('Leagues laden…')}</p> : null}
      {publicLeaguesQuery.isError ? <p className="mt-5 text-sm text-red-300">{t('Manager-leagues konden niet worden geladen.')}</p> : null}
      {!publicLeaguesQuery.isLoading && !publicLeaguesQuery.isError && items.length === 0 ? <p className="mt-5 text-sm text-surface-500">{t('Geen openbare Manager-leagues gevonden.')}</p> : null}

      {items.length > 0 ? (
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          {items.map((league) => <ManagerLeagueDiscoveryCard key={league.id} league={league} />)}
        </div>
      ) : null}

      {publicLeaguesQuery.hasNextPage ? (
        <div ref={loadMoreRef} className="flex min-h-12 items-center justify-center pt-4 text-xs text-surface-500">
          {publicLeaguesQuery.isFetchingNextPage ? t('Meer leagues laden…') : null}
        </div>
      ) : null}
    </div>
  )
}

function ManagerLeagueDiscoveryCard({ league }: { league: import('@/features/manager/api').PublicManagerLeague }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const join = useJoinManagerLeague(league.id)
  const membershipStatus = league.my_membership?.status
  const joined = membershipStatus === 'accepted'
  const pending = membershipStatus === 'pending'
  const competitionLogo = league.competition?.logo_url ?? league.competition?.image_path

  return (
    <article className="flex flex-wrap items-center gap-3 rounded-xl border border-white/[0.06] bg-[#111416] p-3.5">
      <ManagerCompetitionImage src={competitionLogo} fallback="⚽" />
      <div className="min-w-0 flex-1">
        <h3 className="truncate text-sm font-black text-white">{league.name}</h3>
        <p className="mt-1 truncate text-xs text-surface-500">{[league.competition?.name, league.season_name].filter(Boolean).join(' • ')}</p>
        <p className="mt-1 text-[11px] text-surface-500">{league.members_count ?? 0} {t('deelnemers')}</p>
      </div>
      {joined ? (
        <button type="button" onClick={() => navigate(`/dashboard/manager/competities/${league.id}`)} className="shrink-0 rounded-lg border border-accent-400/30 px-3 py-2 text-xs font-bold text-accent-200">{t('Openen')}</button>
      ) : pending ? (
        <span className="shrink-0 rounded-lg border border-amber-300/20 px-3 py-2 text-xs font-bold text-amber-200">{t('In afwachting')}</span>
      ) : (
        <button
          type="button"
          disabled={join.isPending}
          onClick={() => join.mutate()}
          className="shrink-0 rounded-lg bg-accent-600 px-3 py-2 text-xs font-bold text-white transition hover:bg-accent-500 disabled:opacity-60"
        >
          {join.isPending ? t('Bezig…') : league.requires_approval ? t('Aanvragen') : t('Deelnemen')}
        </button>
      )}
      {join.isError ? <p className="basis-full text-xs text-red-300">{join.error.message}</p> : null}
    </article>
  )
}

function ManagerHero({
  home,
  availableLeagueCount,
  onOpenDiscovery,
}: {
  home: ManagerHome | undefined
  availableLeagueCount: number | null | undefined
  onOpenDiscovery: () => void
}) {
  const { t } = useTranslation()
  const summary = home?.summary
  const activeLeagues = summary?.active_leagues_count ?? summary?.joined_leagues_count ?? 0
  const availableLeagues = availableLeagueCount ?? activeLeagues

  return (
    <button
      type="button"
      onClick={onOpenDiscovery}
      aria-haspopup="dialog"
      className="group block h-fit w-full max-w-xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400"
    >
      <div className="flex w-full max-w-xl items-center gap-4 rounded-[1.25rem] border border-white/[0.07] bg-[#0d1113] p-5 text-left transition group-hover:border-accent-300/25 group-hover:bg-[#111719] sm:p-6">
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-surface-300">
            {t('{count} competities beschikbaar', { count: availableLeagues })}
          </span>
          <span className="mt-1 block text-base font-black tracking-tight text-white">
            {t('Competities verkennen')}
          </span>
        </span>
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-surface-500 transition group-hover:translate-x-0.5 group-hover:text-white" aria-hidden="true">
          <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none">
            <path d="m6 3 5 5-5 5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
          </svg>
        </span>
      </div>
    </button>
  )
}

function ManagerDashboard({
  home,
  availableLeagueCount,
  onOpenDiscovery,
}: {
  home: ManagerHome
  availableLeagueCount: number | null | undefined
  onOpenDiscovery: () => void
}) {
  const pendingActions = home.pending_actions ?? []
  const homeLeagues = home.my_leagues ?? []
  const manageableLeagues = (home.my_leagues ?? []).filter(
    (league) =>
      Boolean(league.fantasy_league_id) &&
      ['needs_base_team', 'changes_available'].includes(league.status?.key ?? ''),
  )
  const manageableLeaguesForPicker = manageableLeagues.length > 0
    ? manageableLeagues
    : pendingActions
        .filter((action) => Boolean(action.fantasy_league_id))
        .map((action) =>
          homeLeagues.find((league) => league.fantasy_league_id === action.fantasy_league_id) ?? {
            fantasy_league_id: action.fantasy_league_id,
            league_name: action.league_name,
            season: action.season,
            competition: action.competition,
            status: { key: action.status_key },
          },
        )

  return (
    <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
      <div className="self-start space-y-6">
        <div>
          {manageableLeaguesForPicker.length > 0 ? (
            <ManageTeamsCard
              leagues={manageableLeaguesForPicker}
              previews={home.manager_league_previews}
              previewTotal={home.manager_league_previews_total}
            />
          ) : (
            <CompletedState
              previews={home.manager_league_previews}
              previewTotal={home.manager_league_previews_total}
            />
          )}
        </div>
        {pendingActions.length > 0 ? <ManagerNotificationCenter actions={pendingActions} /> : null}
      </div>

      <div className="self-start">
        <ManagerChallengesLink />
        <div className="mt-6">
        <ManagerHero
          home={home}
          availableLeagueCount={availableLeagueCount}
          onOpenDiscovery={onOpenDiscovery}
        />
        </div>
      </div>
    </div>
  )
}

function ManagerChallengesLink() {
  const { t } = useTranslation()

  return (
    <Link
      to="/dashboard/manager/challenges"
      className="group flex items-center justify-between gap-4 rounded-[1.25rem] border border-accent-300/20 bg-accent-300/[0.05] p-5 transition hover:border-accent-300/40 hover:bg-accent-300/[0.09]"
    >
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-accent-200">{t('Nieuwe spelvorm')}</p>
        <h2 className="mt-1 text-lg font-black tracking-tight text-white">{t('Challenges')}</h2>
        <p className="mt-1 text-sm text-surface-400">{t('Bouw een team met bijzondere regels.')}</p>
      </div>
      <span className="text-xl text-accent-200 transition group-hover:translate-x-1" aria-hidden="true">→</span>
    </Link>
  )
}

function ManagerLeaguePreviewRow({
  leagues,
  total,
  onOpenPicker,
}: {
  leagues: ManagerLeaguePreview[]
  total: number
  onOpenPicker?: () => void
}) {
  const { t } = useTranslation()
  const visibleLeagues = leagues.slice(0, 5)
  const totalLeagues = Math.max(total, visibleLeagues.length)
  const remaining = Math.max(totalLeagues - visibleLeagues.length, 0)

  return (
    <div className="border-t border-white/[0.07] px-5 pb-4 pt-3 sm:px-6">
      <div className="flex items-start gap-3 overflow-x-auto pb-1">
        {visibleLeagues.map((league) => {
          const logo = league.competition?.logo_url ?? league.competition?.image_path
          const title = league.league_name || t('Manager-competitie')

          return (
            <Link
              key={league.fantasy_league_id}
              to={`/dashboard/manager/competities/${league.fantasy_league_id}`}
              aria-label={title}
              className="group flex w-16 shrink-0 flex-col items-center gap-2 rounded-xl p-1 transition hover:bg-white/[0.04]"
            >
              <ManagerLeaguePreviewLogo src={logo} />
              <span className="w-full truncate text-center text-[10px] font-semibold text-surface-400 transition group-hover:text-white">
                {title}
              </span>
            </Link>
          )
        })}
        {remaining > 0 && onOpenPicker ? (
          <button
            type="button"
            onClick={onOpenPicker}
            aria-label={t('Meer teams beheren')}
            title={`+${remaining}`}
            className="flex w-16 shrink-0 flex-col items-center gap-2 rounded-xl p-1 text-left transition hover:bg-white/[0.04]"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-accent-300/25 bg-accent-300/10 text-xs font-black text-accent-200">
              +{remaining}
            </span>
            <span aria-hidden="true" className="h-[15px] w-full" />
          </button>
        ) : null}
      </div>
    </div>
  )
}

function ManagerNotificationCenter({ actions }: { actions: ManagerPendingAction[] }) {
  const { t } = useTranslation()
  const needsBaseTeamCount = actions.filter((action) => action.status_key === 'needs_base_team').length
  const changesAvailableCount = actions.filter((action) => action.status_key === 'changes_available').length

  return (
    <section className="rounded-[1.5rem] border border-amber-300/15 bg-[#0d1113] p-5 sm:p-6">
      <div className="flex items-start gap-3">
        <span className="manager-notification-icon mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center text-lg text-amber-200" aria-hidden="true">
          !
        </span>
        <div className="min-w-0">
          <p className="manager-notification-label text-[10px] font-bold uppercase tracking-[0.18em] text-amber-200">{t('Notificaties')}</p>
          <h2 className="mt-1 text-xl font-black tracking-tight text-white">{t('Openstaande acties')}</h2>
          <p className="mt-2 text-sm leading-6 text-surface-400">{t('Je hebt nog openstaande Manager-acties.')}</p>
        </div>
      </div>

      <div className="mt-5 divide-y divide-white/[0.07] border-y border-white/[0.07]">
        {needsBaseTeamCount > 0 ? (
          <div className="flex items-center gap-3 py-3 first:pt-3">
            <span className="manager-notification-icon flex h-8 w-8 shrink-0 items-center justify-center text-sm text-amber-200" aria-hidden="true">＋</span>
            <p className="text-sm font-semibold text-white">
              {needsBaseTeamCount === 1
                ? t('1 team heeft nog geen opstelling')
                : t('{count} teams hebben nog geen opstelling', { count: needsBaseTeamCount })}
            </p>
          </div>
        ) : null}
        {changesAvailableCount > 0 ? (
          <div className="flex items-center gap-3 py-3 last:pb-3">
            <span className="manager-notification-icon flex h-8 w-8 shrink-0 items-center justify-center text-sm text-amber-200" aria-hidden="true">↻</span>
            <p className="text-sm font-semibold text-white">
              {changesAvailableCount === 1
                ? t('1 team heeft beschikbare wijzigingen')
                : t('{count} teams hebben beschikbare wijzigingen', { count: changesAvailableCount })}
            </p>
          </div>
        ) : null}
      </div>
    </section>
  )
}

function ManageTeamsCard({
  leagues,
  previews,
  previewTotal,
}: {
  leagues: ManagerHomeLeague[]
  previews: ManagerLeaguePreview[]
  previewTotal: number
}) {
  const { t } = useTranslation()
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const manageLabel = t('Mijn opstellingen')
  const manageActionLabel = t('Beheren')

  return (
    <>
      <section className="overflow-hidden rounded-[1.5rem] border border-accent-300/20 bg-[#0d1113] shadow-[0_18px_50px_rgba(0,0,0,0.18)]">
        <div className="flex flex-col gap-5 p-5 sm:p-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-start gap-4">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-accent-200">{t('Actie nodig')}</p>
              <h2 className="mt-1 text-xl font-black tracking-tight text-white sm:text-2xl">{manageLabel}</h2>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setIsPickerOpen(true)}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-accent-600 px-5 py-3 text-sm font-bold text-white transition hover:bg-accent-500"
          >
            {manageActionLabel} <span aria-hidden="true">→</span>
          </button>
        </div>
        {previews.length > 0 ? (
          <ManagerLeaguePreviewRow
            leagues={previews}
            total={previewTotal}
            onOpenPicker={() => setIsPickerOpen(true)}
          />
        ) : null}

      </section>

      {isPickerOpen ? <ManagerTeamPicker leagues={leagues} onClose={() => setIsPickerOpen(false)} /> : null}
    </>
  )
}

function ManagerTeamPicker({ leagues, onClose }: { leagues: ManagerHomeLeague[]; onClose: () => void }) {
  const { t } = useTranslation()
  const [isClosing, setIsClosing] = useState(false)

  function requestClose() {
    setIsClosing(true)
  }

  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') requestClose()
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', handleEscape)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleEscape)
    }
  }, [])

  useEffect(() => {
    if (!isClosing) return

    const timeout = window.setTimeout(onClose, 300)
    return () => window.clearTimeout(timeout)
  }, [isClosing, onClose])

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        onClick={requestClose}
        aria-label={t('Sluit teamselectie')}
        className={`${isClosing ? 'animate-drawer-backdrop-out' : 'animate-drawer-backdrop-in'} absolute inset-0 bg-black/80 backdrop-blur-sm`}
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="manager-team-picker-title"
        className={`${isClosing ? 'animate-manager-player-picker-out' : 'animate-manager-player-picker-in'} absolute inset-x-0 bottom-0 mx-auto flex h-[min(70dvh,640px)] max-h-[88dvh] w-full max-w-3xl flex-col overflow-hidden rounded-t-[1.5rem] border-t border-white/[0.1] bg-[#0d1113] shadow-[0_-20px_64px_rgba(0,0,0,0.5)]`}
      >
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-white/[0.07] p-5 sm:p-6">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-accent-200">{t('Golero Manager')}</p>
            <h2 id="manager-team-picker-title" className="mt-1 text-xl font-black tracking-tight text-white sm:text-2xl">
              {t('Voor welke competitie wil je je team beheren?')}
            </h2>
          </div>
          <ModalCloseButton onClick={requestClose} label={t('Sluiten')} className="h-9 w-9" />
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto p-5 sm:p-6">
          <div className="divide-y divide-white/[0.07] border-y border-white/[0.07]">
            {leagues.map((league) => (
              <Link
                key={league.fantasy_league_id}
                to={`/dashboard/manager/competities/${league.fantasy_league_id}`}
                onClick={onClose}
                className="flex items-center gap-3 px-4 py-4 transition first:pt-3 last:pb-3 hover:bg-[#111719] sm:px-5"
              >
                <ManagerCompetitionImage
                  src={league.competition?.logo_url ?? league.competition?.image_path}
                  fallback="⚽"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-white">{league.league_name || t('Manager-competitie')}</p>
                  <p className="mt-1 truncate text-xs text-surface-500">
                    {[league.competition?.name, league.season?.name].filter(Boolean).join(' • ') || t('Opstelling beheren')}
                  </p>
                </div>
                <span className="shrink-0 text-lg text-surface-500" aria-hidden="true">→</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}

function CompletedState({
  previews,
  previewTotal,
}: {
  previews: ManagerLeaguePreview[]
  previewTotal: number
}) {
  const { t } = useTranslation()

  return (
    <section className="rounded-[1.5rem] border border-emerald-300/15 bg-emerald-300/[0.04] p-5 sm:p-6">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-300/10 text-lg text-emerald-300">✓</span>
        <div>
          <h2 className="text-base font-bold text-white">{t('Geen openstaande teams')}</h2>
          <p className="mt-1 text-sm leading-6 text-surface-400">
            {t('Je Manager-teams vragen momenteel geen actie.')}
          </p>
        </div>
      </div>
      {previews.length > 0 ? <ManagerLeaguePreviewRow leagues={previews} total={previewTotal} /> : null}
    </section>
  )
}

function ManagerCompetitionImage({
  src,
  fallback,
}: {
  src: string | null | undefined
  fallback: string
}) {
  const [hasError, setHasError] = useState(false)

  return src && !hasError ? (
    <img src={src} alt="" onError={() => setHasError(true)} className="h-9 w-9 shrink-0 object-contain" />
  ) : (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center text-surface-300">{fallback}</span>
  )
}

function ManagerLeaguePreviewLogo({ src }: { src: string | null | undefined }) {
  const [hasError, setHasError] = useState(false)

  return src && !hasError ? (
    <img
      src={src}
      alt=""
      onError={() => setHasError(true)}
      className="h-12 w-12 rounded-xl border border-white/[0.08] bg-[#172024] object-contain p-1.5"
    />
  ) : (
    <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/[0.08] bg-[#172024] text-lg text-surface-300">
      ⚽
    </span>
  )
}


function ManagerPageSkeleton() {
  const { t } = useTranslation()
  return (
    <div className="space-y-6" aria-label={t('Golero Manager laden')}>
      <div className="h-72 animate-pulse rounded-[1.75rem] bg-[#0d1113]" />
      <div className="h-52 animate-pulse rounded-[1.5rem] bg-[#0d1113]" />
      <div className="h-64 animate-pulse rounded-[1.5rem] bg-[#0d1113]" />
    </div>
  )
}
